import React, { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { getNotStartedTasks, updateBucket } from '../../services/GraphService/GraphService';
import { useAppContext } from '../../hooks/AppContext';
import Button from '@mui/material/Button';
import { useDispatch } from 'react-redux';
import { updateSlots } from '../../features/slots/slotsSlice';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import Logger from '../../utils/Logger';
const OTHER_BUCKETS = [
    { category: 'one', title: 'Bucket 1' },
    { category: 'three', title: 'Bucket 3' },
    { category: 'five', title: 'Bucket 5' },
];
const PickTaskView = ({ slot, slots, open, onClose }) => {
    const dispatch = useDispatch();
    const app = useAppContext();
    const [tasks, setTasks] = React.useState([]);
    const [uncategorizedTasks, setUncategorizedTasks] = React.useState([]);
    const [otherBuckets, setOtherBuckets] = React.useState([]);
    const [savedLocations, setSavedLocations] = React.useState([]);
    const [selectedLocation, setSelectedLocation] = React.useState(null);
    useEffect(() => {
        const locs = localStorage.getItem('com.boushar.locations');
        if (locs) {
            try {
                setSavedLocations(JSON.parse(locs));
            }
            catch (e) { }
        }
    }, []);
    const renderItems = (items) => items
        .slice()
        .sort((a, b) => { var _a, _b; return ((_a = a === null || a === void 0 ? void 0 : a.getTitle()) !== null && _a !== void 0 ? _a : '').localeCompare((_b = b === null || b === void 0 ? void 0 : b.getTitle()) !== null && _b !== void 0 ? _b : ''); })
        .map((item, idx, arr) => {
        const headerTitle = item.getHeaderTitle();
        return (React.createElement(React.Fragment, { key: idx },
            React.createElement(ListItem, { onClick: () => {
                    assignTaskToSlot(item);
                    onClose();
                }, sx: Object.assign({ cursor: 'pointer' }, (headerTitle !== null && { pl: 3 })) },
                React.createElement(ListItemText, Object.assign({ primary: React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                        React.createElement("span", null, item.getTitle()),
                        item.isRecurring() && React.createElement(AutorenewIcon, { fontSize: "small", titleAccess: "Recurring", color: "action" })) }, (headerTitle !== null && { secondary: headerTitle })))),
            idx < arr.length - 1 && React.createElement(Divider, { component: "li" })));
    });
    const assignTaskToSlot = async (task) => {
        var _a;
        slots.updateSlotWithTodo(slot, task);
        dispatch(updateSlots(slots));
        // Set the task's category to match the slot it is going into.
        const category = slot.getCategory();
        if (task.getCategory() !== category) {
            try {
                // updateSlotWithTodo filled the board's copy of the slot, which now carries the task id.
                const filled = (_a = slots.list.find((s) => s.name === slot.name)) !== null && _a !== void 0 ? _a : slot;
                await updateBucket(filled, category, app.authProvider);
            }
            catch (e) {
                Logger.error(`Failed to update category: ${e}`);
            }
        }
    };
    useEffect(() => {
        const getTasks = async () => {
            const alltasks = await getNotStartedTasks(app.authProvider);
            // get the category from the slot index
            let category;
            if (slot.getIndex() === 0) {
                category = 'one';
            }
            else if (slot.getIndex() <= 3 && slot.getIndex() > 0) {
                category = 'three';
            }
            else if (slot.getIndex() > 3) {
                category = 'five';
            }
            // Get all task IDs already assigned to slots
            const slotTaskIds = new Set(slots.list
                .map((s) => { var _a; return (_a = s.tid) === null || _a === void 0 ? void 0 : _a.taskId; })
                .filter((id) => id !== undefined));
            const matchesLocation = (task) => {
                var _a;
                if (!selectedLocation) {
                    return true;
                }
                const geoString = JSON.stringify({ latitude: selectedLocation.latitude, longitude: selectedLocation.longitude });
                const content = ((_a = task.getBody()) === null || _a === void 0 ? void 0 : _a.content) || "";
                const regex = /{"latitude":[-0-9]*\.[0-9]*,"longitude":[-0-9]*\.[0-9]*}/g;
                const match = content.match(regex);
                return match ? match.includes(geoString) : false;
            };
            const isInSameBucket = (task) => task.getCategories().find((c) => c === category);
            // Filter by category AND exclude tasks already in slots
            const categorized = alltasks.filter((task) => matchesLocation(task) && isInSameBucket(task) &&
                !slotTaskIds.has(task.getId().taskId) && task.getStatus() !== 'completed'); // getNotStartedTasks should already filter ChildItems.
            const uncategorized = alltasks.filter((task) => {
                const cats = task.getCategories();
                return matchesLocation(task) && (!cats || cats.length === 0 || cats.every((c) => c === undefined)) &&
                    !slotTaskIds.has(task.getId().taskId);
            });
            // The other buckets, in 1-3-5 order. A task shows once: in the slot's own bucket if it has it, else under its highest-priority bucket.
            const others = OTHER_BUCKETS
                .filter((bucket) => bucket.category !== category)
                .map((bucket) => (Object.assign(Object.assign({}, bucket), { tasks: alltasks.filter((task) => matchesLocation(task) && !isInSameBucket(task) && task.getCategory() === bucket.category &&
                    !slotTaskIds.has(task.getId().taskId) && task.getStatus() !== 'completed') })))
                .filter((group) => group.tasks.length > 0);
            setTasks(categorized);
            setUncategorizedTasks(uncategorized);
            setOtherBuckets(others);
        };
        getTasks();
    }, [slot, app.authProvider, selectedLocation]);
    return (React.createElement(Dialog, { open: open, onClose: onClose, fullWidth: true, maxWidth: "sm" },
        React.createElement(DialogTitle, null, "Pick Task"),
        React.createElement(DialogContent, null,
            React.createElement(Autocomplete, { options: savedLocations, getOptionLabel: (option) => option.title, value: selectedLocation, onChange: (e, newValue) => setSelectedLocation(newValue), renderInput: (params) => React.createElement(TextField, Object.assign({}, params, { label: "Filter by Location", size: "small", variant: "standard" })), sx: { mb: 2 } }),
            React.createElement(List, null, renderItems(tasks)),
            uncategorizedTasks.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement("h3", null, "Uncategorized Tasks"),
                React.createElement(List, null, renderItems(uncategorizedTasks)))),
            otherBuckets.map((group) => (React.createElement(React.Fragment, { key: group.category },
                React.createElement("h3", null, group.title),
                React.createElement(List, null, renderItems(group.tasks))))),
            React.createElement(Button, { onClick: onClose, color: "primary", variant: "outlined", fullWidth: true, style: { marginTop: 16 } }, "Cancel"))));
};
export default PickTaskView;
